import { useState } from "react";

const AMBER = "oklch(var(--primary))";
const MUTED_TEXT = "oklch(0.48 0.012 75)";

const supplierRows = [
  {
    supplier: "Cloudflare",
    exceptions: "4",
    evidence: "invoice_042.pdf",
  },
  {
    supplier: "Acme GmbH",
    exceptions: "3",
    evidence: "contract_terms.csv",
  },
  {
    supplier: "Datadog",
    exceptions: "3",
    evidence: "stripe_event_91",
  },
];

const configuredWorkflowSteps = [
  "Extract invoice fields",
  "Match payment events",
  "Check contract terms",
  "Flag exceptions",
  "Update payables record",
];

const requiredSources = [
  { source: "Invoices folder", status: "Connected", accent: false },
  { source: "Stripe payments", status: "Connected", accent: false },
  { source: "Contract terms", status: "Required", accent: true },
];

const ScoutVisual = () => {
  const [mode, setMode] = useState<"query" | "setup">("setup");

  return (
    <div className="rounded-2xl border border-border bg-surface-panel overflow-hidden select-none w-full">

      {/* Header */}
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-primary" />
          <span className="font-mono text-[0.62rem] tracking-[0.18em] uppercase text-primary">Scout</span>
        </div>
        <div
          className="flex items-center gap-0.5 p-0.5 rounded-full border border-border"
          style={{ background: "oklch(0.94 0.006 75 / 0.5)" }}
        >
          <button
            onClick={() => setMode("setup")}
            className="font-mono text-[0.55rem] tracking-[0.1em] uppercase px-2.5 py-1 rounded-full transition-colors duration-150"
            style={
              mode === "setup"
                ? { background: "white", color: "oklch(0.12 0.008 75)", boxShadow: "0 1px 3px oklch(0.12 0.008 75 / 0.12)" }
                : { color: MUTED_TEXT }
            }
          >
            Setup workflow
          </button>
          <button
            onClick={() => setMode("query")}
            className="font-mono text-[0.55rem] tracking-[0.1em] uppercase px-2.5 py-1 rounded-full transition-colors duration-150"
            style={
              mode === "query"
                ? { background: "white", color: "oklch(0.12 0.008 75)", boxShadow: "0 1px 3px oklch(0.12 0.008 75 / 0.12)" }
                : { color: MUTED_TEXT }
            }
          >
            Query records
          </button>
        </div>
      </div>

      {mode === "query" ? (
        <>
          {/* Ask */}
          <div className="px-4 pt-4 pb-3">
            <div
              className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl border"
              style={{ background: "oklch(0.94 0.006 75 / 0.4)", borderColor: "oklch(var(--primary) / 0.25)" }}
            >
              <span className="text-[0.8rem] text-foreground leading-[1.45]">
                Which suppliers consistently missed payments in the past 2 quarters?
              </span>
            </div>
          </div>

          {/* Answer */}
          <div
            data-testid="query-answer-panel"
            className="px-5 py-5 border-t border-border"
            style={{ background: "oklch(0.94 0.006 75 / 0.32)" }}
          >
            <p className="font-mono text-[0.58rem] tracking-[0.12em] uppercase text-muted-foreground mb-2">Answer</p>
            <div className="grid grid-cols-[auto_minmax(0,1fr)] items-end gap-3">
              <data
                value="12"
                className="font-mono text-[2.75rem] leading-none tracking-[-0.02em] tabular-nums"
                style={{ color: AMBER }}
              >
                12
              </data>
              <p className="pb-1 text-[0.95rem] leading-[1.2] text-foreground">
                suppliers with repeated payment exceptions
              </p>
            </div>
            <p className="mt-2 font-mono text-[0.58rem] text-muted-foreground">
              using invoice-reconciliation.workflow v2.3 · last run 14m ago
            </p>
          </div>

          {/* Results table */}
          <div className="px-5 py-4 border-t border-border">
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="font-mono text-[0.58rem] tracking-[0.12em] uppercase text-muted-foreground">
                Supplier exception table
              </p>
              <p className="font-mono text-[0.55rem] tracking-[0.1em] uppercase text-muted-foreground">
                3 shown
              </p>
            </div>
            <div className="overflow-x-auto border border-border bg-surface-panel rounded-xl overflow-hidden">
              <table
                aria-label="Supplier exception results"
                className="w-full min-w-[460px] border-collapse"
              >
                <thead>
                  <tr className="border-b border-border" style={{ background: "oklch(0.94 0.006 75 / 0.45)" }}>
                    <th scope="col" className="px-3.5 py-2.5 text-left font-mono text-[0.55rem] tracking-[0.1em] uppercase text-muted-foreground font-normal">
                      Supplier
                    </th>
                    <th scope="col" className="px-3.5 py-2.5 text-left font-mono text-[0.55rem] tracking-[0.1em] uppercase text-muted-foreground font-normal">
                      Exceptions
                    </th>
                    <th scope="col" className="px-3.5 py-2.5 text-left font-mono text-[0.55rem] tracking-[0.1em] uppercase text-muted-foreground font-normal">
                      Source
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {supplierRows.map(({ supplier, exceptions, evidence }) => (
                    <tr key={supplier} className="border-b border-border last:border-b-0">
                      <td className="px-3.5 py-3 font-mono text-[0.62rem] font-medium text-foreground">
                        {supplier}
                      </td>
                      <td className="px-3.5 py-3 font-mono text-[0.62rem] tabular-nums" style={{ color: AMBER }}>
                        {exceptions}
                      </td>
                      <td className="px-3.5 py-3 font-mono text-[0.6rem] text-muted-foreground">
                        {evidence}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </>
      ) : (
        <>
          {/* Workflow intent */}
          <div className="px-5 pt-5 pb-4">
            <p className="font-mono text-[0.58rem] tracking-[0.12em] uppercase text-muted-foreground mb-2">
              Workflow intent
            </p>
            <div className="rounded-xl border border-border bg-muted/60 px-4 py-3">
              <p className="text-[0.86rem] text-foreground leading-[1.45]">
                Review new supplier invoices against payments and contracts.
              </p>
            </div>
          </div>

          {/* Registry match */}
          <div className="px-5 py-4 border-t border-border">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-mono text-[0.58rem] tracking-[0.12em] uppercase text-muted-foreground mb-2">
                  Registry match
                </p>
                <p className="font-serif text-[1rem] font-semibold leading-tight text-foreground">
                  Invoice reconciliation workflow found
                </p>
                <p className="mt-1 font-mono text-[0.58rem] text-muted-foreground">
                  Based on existing payables logic · v2.3
                </p>
              </div>
              <span
                className="mt-0.5 shrink-0 rounded-full border px-2.5 py-1 font-mono text-[0.55rem] uppercase tracking-[0.1em]"
                style={{
                  borderColor: "oklch(var(--primary) / 0.28)",
                  color: AMBER,
                  background: "oklch(var(--primary) / 0.06)",
                }}
              >
                Reused
              </span>
            </div>
          </div>

          {/* Workflow configured */}
          <div
            className="px-5 py-4 border-t border-border"
            style={{ background: "oklch(0.94 0.006 75 / 0.28)" }}
          >
            <p className="font-mono text-[0.58rem] tracking-[0.12em] uppercase mb-2" style={{ color: AMBER }}>
              Workflow configured
            </p>
            <p className="max-w-[58ch] text-[0.82rem] leading-[1.55] text-muted-foreground">
              Scout adapted the workflow for supplier invoices, payment events, contract terms, and exception review.
            </p>
          </div>

          {/* Configured logic */}
          <div className="px-5 py-4 border-t border-border">
            <p className="font-mono text-[0.58rem] tracking-[0.12em] uppercase text-muted-foreground mb-3">
              Configured logic
            </p>
            <div className="overflow-x-auto">
              <div
                data-testid="configured-workflow-surface"
                className="grid min-w-[560px] grid-cols-5 divide-x divide-border border border-border rounded-xl overflow-hidden"
              >
                {configuredWorkflowSteps.map((step, index) => (
                  <div
                    key={step}
                    data-testid="configured-workflow-step"
                    className="px-3 py-3"
                    style={{
                      background: index === 1 ? "oklch(var(--primary) / 0.07)" : "oklch(0.975 0.003 75)",
                    }}
                  >
                    <p
                      className="mb-2 font-mono text-[0.52rem] tabular-nums"
                      style={{ color: index === 1 ? AMBER : MUTED_TEXT }}
                    >
                      {String(index + 1).padStart(2, "0")}
                    </p>
                    <p
                      className="font-mono text-[0.58rem] leading-[1.35]"
                      style={{ color: index === 1 ? AMBER : "oklch(0.12 0.008 75)" }}
                    >
                      {step}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Required sources */}
          <div className="px-5 py-4 border-t border-border">
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="font-mono text-[0.58rem] tracking-[0.12em] uppercase text-muted-foreground">
                Required sources
              </p>
              <p className="font-mono text-[0.55rem] tracking-[0.1em] uppercase" style={{ color: AMBER }}>
                1 required
              </p>
            </div>
            <div className="overflow-hidden rounded-xl border border-border">
              <table aria-label="Required workflow sources" className="w-full border-collapse">
                <tbody>
                  {requiredSources.map(({ source, status, accent }) => (
                    <tr key={source} className="border-b border-border last:border-b-0">
                      <td className="px-3.5 py-3 font-mono text-[0.62rem] text-foreground">
                        {source}
                      </td>
                      <td
                        className="px-3.5 py-3 text-right font-mono text-[0.58rem]"
                        style={{ color: accent ? AMBER : "oklch(0.45 0.012 75)" }}
                      >
                        {status}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div
            className="px-5 py-3 border-t flex items-center justify-between gap-4"
            style={{ borderColor: "oklch(var(--primary) / 0.18)", background: "oklch(var(--primary) / 0.04)" }}
          >
            <span className="font-mono text-[0.58rem] text-muted-foreground">
              Contract terms needed before the first run.
            </span>
            <button
              type="button"
              className="shrink-0 rounded-full px-3 py-1.5 font-mono text-[0.58rem] uppercase tracking-[0.08em] text-foreground transition-transform duration-200 active:scale-[0.98]"
              style={{
                background: "white",
                boxShadow: "inset 0 0 0 1px oklch(0.80 0.014 75), 0 10px 18px -18px oklch(0.18 0.012 75 / 0.45)",
              }}
            >
              Continue to source setup
            </button>
          </div>
        </>
      )}

    </div>
  );
};

export default ScoutVisual;
